import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Users, Plus, Trash2, Play, DollarSign, Loader2, Download, Mail, Pencil, TrendingUp, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const US_STATES_SHORT = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

const PayrollTab = ({ businessId, token }) => {
  const [employees, setEmployees] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showRunPayroll, setShowRunPayroll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailingId, setEmailingId] = useState(null);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState({
    name: '', email: '', pay_type: 'salary', pay_rate: '',
    filing_status: 'single', state: 'CA', health_insurance: '0', retirement_401k: '0'
  });
  const [payrollForm, setPayrollForm] = useState({ pay_period_start: '', pay_period_end: '' });
  const [hoursMap, setHoursMap] = useState({});
  const { toast } = useToast();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchEmployees = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/employees/${businessId}`, authHeaders);
      setEmployees(res.data);
    } catch (error) { console.error("PayrollTab error:", error); }
  }, [businessId, token]);

  const fetchPayrollHistory = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/payroll/${businessId}`, authHeaders);
      setPayrollHistory(res.data);
    } catch (error) { console.error("PayrollTab error:", error); }
  }, [businessId, token]);

  const fetchSummary = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/payroll/summary/${businessId}`, authHeaders);
      setSummary(res.data);
    } catch (error) { console.error("PayrollTab summary error:", error); }
  }, [businessId, token]);

  useEffect(() => { fetchEmployees(); fetchPayrollHistory(); fetchSummary(); }, [fetchEmployees, fetchPayrollHistory, fetchSummary]);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/mybookie/employees`, {
        ...empForm, business_id: businessId,
        pay_rate: parseFloat(empForm.pay_rate),
        health_insurance: parseFloat(empForm.health_insurance) || 0,
        retirement_401k: parseFloat(empForm.retirement_401k) || 0,
      }, authHeaders);
      toast({ title: "Employee added" });
      setShowAddEmployee(false);
      resetEmpForm();
      fetchEmployees();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    if (!editingEmp) return;
    setLoading(true);
    try {
      await axios.put(`${API}/api/mybookie/employees/${editingEmp.id}`, {
        name: empForm.name, email: empForm.email, pay_type: empForm.pay_type,
        pay_rate: parseFloat(empForm.pay_rate),
        filing_status: empForm.filing_status, state: empForm.state,
        health_insurance: parseFloat(empForm.health_insurance) || 0,
        retirement_401k: parseFloat(empForm.retirement_401k) || 0,
      }, authHeaders);
      toast({ title: "Employee updated" });
      setShowEditEmployee(false);
      setEditingEmp(null);
      fetchEmployees();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const openEditDialog = (emp) => {
    setEditingEmp(emp);
    setEmpForm({
      name: emp.name, email: emp.email, pay_type: emp.pay_type,
      pay_rate: String(emp.pay_rate), filing_status: emp.filing_status || 'single',
      state: emp.state || 'CA',
      health_insurance: String(emp.health_insurance || 0),
      retirement_401k: String(emp.retirement_401k || 0),
    });
    setShowEditEmployee(true);
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Deactivate this employee?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/employees/${id}`, authHeaders);
      toast({ title: "Employee deactivated" });
      fetchEmployees();
    } catch (error) { console.error("PayrollTab error:", error); }
  };

  const handleRunPayroll = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/payroll/run`, {
        business_id: businessId, ...payrollForm, hours: hoursMap,
      }, authHeaders);
      toast({ title: "Payroll processed!", description: `Total: $${res.data.total_gross?.toLocaleString()}` });
      setShowRunPayroll(false);
      fetchPayrollHistory();
      fetchSummary();
    } catch (err) {
      toast({ title: "Error", description: "Failed to run payroll", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleEmailPaystubs = async (payrollId) => {
    setEmailingId(payrollId);
    try {
      const res = await axios.post(`${API}/api/mybookie/payroll/email-paystubs/${payrollId}`, {}, authHeaders);
      toast({ title: "Paystubs emailed", description: `Sent: ${res.data.sent}, Failed: ${res.data.failed}` });
    } catch (err) {
      toast({ title: "Error", description: "Failed to email paystubs", variant: "destructive" });
    } finally { setEmailingId(null); }
  };

  const resetEmpForm = () => setEmpForm({
    name: '', email: '', pay_type: 'salary', pay_rate: '',
    filing_status: 'single', state: 'CA', health_insurance: '0', retirement_401k: '0'
  });

  const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const hourlyEmployees = employees.filter(e => e.pay_type === 'hourly');

  return (
    <div className="space-y-6" data-testid="payroll-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#111614]">Payroll</h2>
          <p className="text-sm text-[#5C6661]">{employees.length} active employee{employees.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { resetEmpForm(); setShowAddEmployee(true); }} className="rounded-sm" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Employee
          </Button>
          <Button size="sm" onClick={() => setShowRunPayroll(true)} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={employees.length === 0} data-testid="run-payroll-btn">
            <Play className="w-4 h-4 mr-1" /> Run Payroll
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && summary.total_runs > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-[#5C6661]">YTD Gross Payroll</p>
              <p className="text-lg font-semibold text-[#111614]" data-testid="payroll-ytd-gross">{fmt(summary.total_gross)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-[#5C6661]">YTD Taxes</p>
              <p className="text-lg font-semibold text-red-600" data-testid="payroll-ytd-taxes">{fmt(summary.total_taxes)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-[#5C6661]">YTD Net</p>
              <p className="text-lg font-semibold text-green-600">{fmt(summary.total_net)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-[#5C6661]">Payroll Runs</p>
              <p className="text-lg font-semibold text-[#111614]">{summary.total_runs}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-[#5C6661]">Avg Cost/Run</p>
              <p className="text-lg font-semibold text-[#111614]">{fmt(summary.avg_payroll_cost)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payroll Cost Trend */}
      {summary?.monthly_trend?.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Monthly Payroll Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {summary.monthly_trend.map((m) => {
                const maxVal = Math.max(...summary.monthly_trend.map(t => t.gross));
                const pct = maxVal > 0 ? (m.gross / maxVal) * 100 : 0;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-[#0A2B1D] rounded-t" style={{ height: `${Math.max(pct, 4)}%` }} title={`${m.month}: ${fmt(m.gross)}`} />
                    <span className="text-[10px] text-[#8A948F]">{m.month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee List */}
      <Card>
        <CardContent className="p-0">
          {employees.length === 0 ? (
            <div className="text-center py-8 text-[#5C6661]">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>No employees yet. Add your first employee to get started.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[#F6F5F2]">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-right py-3 px-4">Pay Rate</th>
                  <th className="text-left py-3 px-4">State</th>
                  <th className="text-right py-3 px-4">Pre-tax</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{emp.name}</td>
                    <td className="py-3 px-4 text-[#5C6661]">{emp.email}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 bg-[#E2ECE7] text-[#0A2B1D] rounded-full text-xs capitalize">{emp.pay_type}</span>
                    </td>
                    <td className="py-3 px-4 text-right">{emp.pay_type === 'salary' ? `${fmt(emp.pay_rate)}/yr` : `${fmt(emp.pay_rate)}/hr`}</td>
                    <td className="py-3 px-4 text-xs text-[#5C6661]">{emp.state || 'N/A'}</td>
                    <td className="py-3 px-4 text-right text-xs text-[#5C6661]">
                      {(emp.health_insurance > 0 || emp.retirement_401k > 0) ? (
                        <span>{emp.health_insurance > 0 ? `HI:${fmt(emp.health_insurance)}` : ''}{emp.retirement_401k > 0 ? ` 401k:${emp.retirement_401k}%` : ''}</span>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-4 text-right flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#0A2B1D]" onClick={() => openEditDialog(emp)} data-testid={`edit-employee-${emp.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => handleDeleteEmployee(emp.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Payroll History */}
      {payrollHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payroll History</CardTitle>
          </CardHeader>
          <CardContent>
            {payrollHistory.map((run) => (
              <div key={run.id} className="border rounded mb-4 last:mb-0">
                <div className="flex items-center justify-between p-4 bg-[#F6F5F2] border-b">
                  <div>
                    <p className="font-medium text-sm">{run.pay_period_start} to {run.pay_period_end}</p>
                    <p className="text-xs text-[#5C6661]">{run.pay_stubs?.length || 0} employee{(run.pay_stubs?.length || 0) !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span>Gross: <span className="font-medium">{fmt(run.total_gross)}</span></span>
                    <span>Taxes: <span className="font-medium text-red-600">{fmt(run.total_taxes)}</span></span>
                    <span>Net: <span className="font-semibold text-green-600">{fmt(run.total_net)}</span></span>
                    <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => handleEmailPaystubs(run.id)}
                      disabled={emailingId === run.id} data-testid={`email-paystubs-${run.id}`}>
                      {emailingId === run.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
                      Email All
                    </Button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs text-[#5C6661]">
                      <th className="text-left py-2 px-3">Employee</th>
                      <th className="text-right py-2 px-3">Gross</th>
                      <th className="text-right py-2 px-3">Pre-tax</th>
                      <th className="text-right py-2 px-3">Fed Tax</th>
                      <th className="text-right py-2 px-3">State</th>
                      <th className="text-right py-2 px-3">FICA</th>
                      <th className="text-right py-2 px-3">Net</th>
                      <th className="text-right py-2 px-3">YTD Gross</th>
                      <th className="text-center py-2 px-3">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(run.pay_stubs || []).map((stub) => (
                      <tr key={stub.employee_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <span className="font-medium">{stub.employee_name}</span>
                          {stub.state && <span className="text-xs text-[#8A948F] ml-1.5">({stub.state})</span>}
                        </td>
                        <td className="py-2 px-3 text-right">{fmt(stub.gross_pay)}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{stub.pre_tax_deductions > 0 ? fmt(stub.pre_tax_deductions) : '—'}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{fmt(stub.federal_tax)}</td>
                        <td className="py-2 px-3 text-right text-blue-600">{fmt(stub.state_tax)}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{fmt(stub.social_security + stub.medicare + (stub.medicare_additional || 0))}</td>
                        <td className="py-2 px-3 text-right font-medium text-green-600">{fmt(stub.net_pay)}</td>
                        <td className="py-2 px-3 text-right text-xs text-[#8A948F]">{fmt(stub.ytd_gross)}</td>
                        <td className="py-2 px-3 text-center">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            data-testid={`download-paystub-${stub.employee_id}`}
                            onClick={() => window.open(`${API}/api/mybookie/payroll/paystub/${run.id}/${stub.employee_id}`, '_blank')}>
                            <Download className="w-3.5 h-3.5 text-[#0A2B1D]" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Employee Dialog */}
      {[
        { open: showAddEmployee, setOpen: setShowAddEmployee, title: "Add Employee", onSubmit: handleAddEmployee, btnLabel: "Add Employee" },
        { open: showEditEmployee, setOpen: (v) => { setShowEditEmployee(v); if (!v) setEditingEmp(null); }, title: "Edit Employee", onSubmit: handleEditEmployee, btnLabel: "Save Changes" },
      ].map((dlg) => (
        <Dialog key={dlg.title} open={dlg.open} onOpenChange={dlg.setOpen}>
          <DialogContent data-testid={dlg.title === "Add Employee" ? "add-employee-dialog" : "edit-employee-dialog"}>
            <DialogHeader><DialogTitle>{dlg.title}</DialogTitle></DialogHeader>
            <form onSubmit={dlg.onSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} required data-testid="emp-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} required data-testid="emp-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pay Type</Label>
                  <Select value={empForm.pay_type} onValueChange={(v) => setEmpForm({ ...empForm, pay_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salary">Salary</SelectItem>
                      <SelectItem value="hourly">Hourly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{empForm.pay_type === 'salary' ? 'Annual Salary ($)' : 'Hourly Rate ($)'}</Label>
                  <Input type="number" step="0.01" value={empForm.pay_rate} onChange={(e) => setEmpForm({ ...empForm, pay_rate: e.target.value })} required data-testid="emp-pay-rate" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Filing Status</Label>
                  <Select value={empForm.filing_status} onValueChange={(v) => setEmpForm({ ...empForm, filing_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="head_of_household">Head of Household</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State</Label>
                  <Select value={empForm.state} onValueChange={(v) => setEmpForm({ ...empForm, state: v })}>
                    <SelectTrigger data-testid="emp-state"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {US_STATES_SHORT.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Health Insurance ($/period)</Label>
                  <Input type="number" step="0.01" value={empForm.health_insurance} onChange={(e) => setEmpForm({ ...empForm, health_insurance: e.target.value })} data-testid="emp-health-ins" />
                  <p className="text-[10px] text-[#8A948F] mt-1">Pre-tax deduction per pay period</p>
                </div>
                <div>
                  <Label>401(k) Contribution (%)</Label>
                  <Input type="number" step="0.5" min="0" max="100" value={empForm.retirement_401k} onChange={(e) => setEmpForm({ ...empForm, retirement_401k: e.target.value })} data-testid="emp-401k" />
                  <p className="text-[10px] text-[#8A948F] mt-1">% of gross pay</p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => dlg.setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={loading} data-testid="save-employee-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : dlg.btnLabel}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ))}

      {/* Run Payroll Dialog */}
      <Dialog open={showRunPayroll} onOpenChange={setShowRunPayroll}>
        <DialogContent className="max-w-lg" data-testid="run-payroll-dialog">
          <DialogHeader><DialogTitle>Run Payroll</DialogTitle></DialogHeader>
          <form onSubmit={handleRunPayroll} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input type="date" value={payrollForm.pay_period_start} onChange={(e) => setPayrollForm({ ...payrollForm, pay_period_start: e.target.value })} required data-testid="payroll-start" />
              </div>
              <div>
                <Label>Period End</Label>
                <Input type="date" value={payrollForm.pay_period_end} onChange={(e) => setPayrollForm({ ...payrollForm, pay_period_end: e.target.value })} required data-testid="payroll-end" />
              </div>
            </div>
            {hourlyEmployees.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Hours for Hourly Employees</Label>
                {hourlyEmployees.map(emp => (
                  <div key={emp.id} className="flex items-center justify-between bg-[#F6F5F2] p-3 rounded">
                    <span className="text-sm">{emp.name} <span className="text-xs text-[#8A948F]">({fmt(emp.pay_rate)}/hr)</span></span>
                    <Input type="number" step="0.5" min="0" className="w-24 text-right" placeholder="80"
                      value={hoursMap[emp.id] || ''}
                      onChange={(e) => setHoursMap({ ...hoursMap, [emp.id]: parseFloat(e.target.value) || 0 })}
                      data-testid={`hours-${emp.id}`} />
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm text-[#5C6661]">
              Processing payroll for {employees.length} employee{employees.length !== 1 ? 's' : ''}.
              Salary employees: bi-weekly rate. Hourly: enter hours above (default 80).
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRunPayroll(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={loading} data-testid="process-payroll-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Process Payroll'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollTab;
